import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper } from '@mui/material';
import { Lightbulb, Folder, Description } from '@mui/icons-material';
import { useAuth } from '@vortx/shared-ui';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';
import { Skeleton } from '../ux';
import { API_BASE_URL } from '../../services/api';

const DEFAULT_TIPS = [
    "Batteries under 2hr duration rarely pencil for demand charge savings",
    "NEM 3.0 projects benefit from west-facing arrays for afternoon peak",
    "Peak shaving works best with 2-4hr battery duration",
    "Load factor below 40% indicates strong demand charge savings potential",
    "Bifacial modules add 5-10% production for minimal cost increase",
];

const LightActivityFeed = ({ currentProject }) => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [projects, setProjects] = useState([]);
    const [scenarios, setScenarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tipIndex, setTipIndex] = useState(0);
    const [tipFading, setTipFading] = useState(false);

    const tips = DEFAULT_TIPS;
    const accentColor = vortxColors.gold;

    // Fetch recent projects
    useEffect(() => {
        const fetchProjects = async () => {
            if (!currentUser) {
                setLoading(false);
                return;
            }
            try {
                const token = await currentUser.getIdToken();
                const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setProjects(data.projects || []);
                }
            } catch (err) {
                console.error('Failed to fetch projects', err);
            } finally {
                setLoading(false);
            }
        };
        fetchProjects();
    }, [currentUser]);

    // Fetch scenarios if we have a current project
    useEffect(() => {
        const fetchScenarios = async () => {
            if (!currentUser || !currentProject?.id) {
                setScenarios([]);
                return;
            }
            try {
                const token = await currentUser.getIdToken();
                const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/${currentProject.id}/scenarios`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    setScenarios(data.scenarios || []);
                }
            } catch (err) {
                console.error('Failed to fetch scenarios', err);
            }
        };
        fetchScenarios();
    }, [currentUser, currentProject]);

    // Rotate tips with animation
    useEffect(() => {
        const interval = setInterval(() => {
            setTipFading(true);
            setTimeout(() => {
                setTipIndex(prev => (prev + 1) % tips.length);
                setTipFading(false);
            }, 300);
        }, 8000);

        return () => clearInterval(interval);
    }, [tips.length]);

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            gap: 2,
        }}>
            {/* Context-aware header */}
            {currentProject ? (
                // Show scenarios when viewing a project
                <Paper elevation={0} sx={{
                    p: 2,
                    bgcolor: 'rgba(0,0,0,0.02)',
                    border: '1px solid rgba(0,0,0,0.08)',
                    borderRadius: 2,
                }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.body,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        color: vortxColors.charcoal,
                        opacity: 0.6,
                        marginBottom: 1.5,
                        paddingBottom: 1,
                        borderBottom: '1px solid rgba(0,0,0,0.08)',
                    }}>
                        Project Scenarios
                    </Typography>
                    {scenarios.length === 0 ? (
                        <Typography sx={{
                            fontFamily: vortxTypography.body,
                            fontSize: '0.8rem',
                            color: vortxColors.charcoal,
                            opacity: 0.5,
                            fontStyle: 'italic',
                        }}>
                            No scenarios yet
                        </Typography>
                    ) : (
                        scenarios.slice(0, 5).map((scenario) => (
                            <Box
                                key={scenario.id}
                                onClick={() => navigate(`/projects/${currentProject.id}/scenarios/${scenario.id}`)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    padding: '0.5rem 0.75rem',
                                    cursor: 'pointer',
                                    borderRadius: '6px',
                                    transition: 'all 0.15s ease',
                                    '&:hover': {
                                        background: `${accentColor}10`,
                                    }
                                }}
                            >
                                <Description sx={{ fontSize: 16, color: accentColor, opacity: 0.7 }} />
                                <Typography sx={{
                                    fontFamily: vortxTypography.body,
                                    fontSize: '0.8rem',
                                    color: vortxColors.charcoal,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}>
                                    {scenario.name}
                                </Typography>
                            </Box>
                        ))
                    )}
                </Paper>
            ) : (
                // Show recent projects when on dashboard or list view
                <Paper elevation={0} sx={{
                    p: 2,
                    bgcolor: 'rgba(0,0,0,0.02)',
                    border: '1px solid rgba(0,0,0,0.08)',
                    borderRadius: 2,
                }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.body,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        color: vortxColors.charcoal,
                        opacity: 0.6,
                        marginBottom: 1.5,
                        paddingBottom: 1,
                        borderBottom: '1px solid rgba(0,0,0,0.08)',
                    }}>
                        Recent Projects
                    </Typography>
                    {loading ? (
                        <Box sx={{ py: 0.5 }}>
                            {[1, 2, 3].map((i) => (
                                <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
                                    <Skeleton variant="circular" width={16} height={16} />
                                    <Skeleton variant="text" className="vx-skeleton-text medium" />
                                </Box>
                            ))}
                        </Box>
                    ) : projects.length === 0 ? (
                        <Typography sx={{
                            fontFamily: vortxTypography.body,
                            fontSize: '0.8rem',
                            color: vortxColors.charcoal,
                            opacity: 0.5,
                            fontStyle: 'italic',
                        }}>
                            No recent projects
                        </Typography>
                    ) : (
                        projects.slice(0, 5).map((project) => (
                            <Box
                                key={project.id}
                                onClick={() => navigate(`/projects/${project.id}`)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    padding: '0.5rem 0.75rem',
                                    cursor: 'pointer',
                                    borderRadius: '6px',
                                    transition: 'all 0.15s ease',
                                    '&:hover': {
                                        background: `${accentColor}10`,
                                    }
                                }}
                            >
                                <Folder sx={{ fontSize: 16, color: accentColor, opacity: 0.7 }} />
                                <Typography sx={{
                                    fontFamily: vortxTypography.body,
                                    fontSize: '0.8rem',
                                    color: vortxColors.charcoal,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}>
                                    {project.name}
                                </Typography>
                            </Box>
                        ))
                    )}
                </Paper>
            )}

            {/* Spacer */}
            <Box sx={{ flex: 1 }} />

            {/* Contextual Tip */}
            <Paper elevation={0} sx={{
                p: 2,
                bgcolor: `${accentColor}08`,
                border: `1px solid ${accentColor}20`,
                borderRadius: 2,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Lightbulb sx={{ fontSize: 16, color: accentColor }} />
                    <Typography sx={{
                        fontFamily: vortxTypography.body,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        color: accentColor,
                    }}>
                        Pro Tip
                    </Typography>
                </Box>
                <Typography sx={{
                    fontFamily: vortxTypography.body,
                    fontSize: '0.8rem',
                    color: vortxColors.charcoal,
                    lineHeight: 1.5,
                    opacity: tipFading ? 0 : 0.8,
                    transform: tipFading ? 'translateY(-5px)' : 'translateY(0)',
                    transition: 'opacity 0.3s ease, transform 0.3s ease',
                }}>
                    {tips[tipIndex]}
                </Typography>
            </Paper>
        </Box>
    );
};

export default LightActivityFeed;
